import RenderIf from "common/components/RenderIf";
import PricingItem from "common/components/subscriptions/PricingItem";
import ToPayNow from "common/components/subscriptions/ToPayNow";
import useCustomerEstimateCreation from "common/datahooks/useCustomerEstimateCreation";
import useCustomerEstimateRenewal from "common/datahooks/useCustomerEstimateRenewal";
import useCustomerEstimateUpdate from "common/datahooks/useCustomerEstimateUpdate";
import usePromotions from "common/datahooks/usePromotions";
import { HTTPError } from "common/helpers/HTTP";
import { formatPrice, formatPricingPeriodInfo } from "common/helpers/utils";
import type { Pricing } from "common/types";

export default function PricingDetails({
  pricing,
  seatCount,
  includedSeats,
  isDowngrade,
  alertsComponent,
}: {
  pricing: { addon: Pricing; plan: Pricing };
  seatCount: number;
  includedSeats: number;
  isDowngrade: boolean;
  alertsComponent: JSX.Element;
}) {
  const { agencyPlan } = usePromotions();
  const { estimateRenewal, estimateRenewalError, isLoadingEstimateRenewal } =
    useCustomerEstimateRenewal(agencyPlan?.subscription);
  const { estimateUpdate, estimateUpdateError, isLoadingEstimateUpdate } =
    useCustomerEstimateUpdate({
      pricing,
      quantity: seatCount,
      prorate: false,
      subscription: agencyPlan?.subscription,
    });
  const {
    estimateUpdate: proRatedEstimateUpdate,
    isLoadingEstimateUpdate: isLoadingProratedEstimateUpdate,
    estimateUpdateError: proRatedEstimateUpdateError,
  } = useCustomerEstimateUpdate({
    pricing,
    quantity: seatCount,
    prorate: true,
    subscription: agencyPlan?.subscription,
  });
  const { estimateCreation, isLoadingEstimateCreation, estimateCreationError } =
    useCustomerEstimateCreation({
      planPricingId: pricing.plan.id,
      quantity: seatCount,
      subscription: agencyPlan?.subscription,
    });
  const error =
    estimateCreationError ||
    estimateUpdateError ||
    estimateRenewalError ||
    proRatedEstimateUpdateError;

  const selectionAdditionalSeats = seatCount - includedSeats;
  const selectionPricing = pricing.plan;
  const isUndefinedOrCancelled =
    !agencyPlan || agencyPlan.subscription.status === "cancelled";

  let currentEstimate = estimateCreation;

  const isLoadingCurrentEstimate =
    isLoadingEstimateCreation ||
    isLoadingEstimateRenewal ||
    isLoadingEstimateUpdate;

  let currentAdditionalSeats = selectionAdditionalSeats;
  let currentPricing = selectionPricing;
  let isPricingPeriodChanged = false;
  let isChange = false;

  if (agencyPlan?.subscription) {
    currentPricing = agencyPlan.subscription.item_price;
    currentAdditionalSeats = agencyPlan.license_count - includedSeats;

    if (agencyPlan.subscription.status === "active") {
      currentEstimate = estimateRenewal;
      isPricingPeriodChanged =
        agencyPlan.subscription.item_price.id !== selectionPricing.id;
      isChange =
        isPricingPeriodChanged || agencyPlan.license_count !== seatCount;
    } else if (agencyPlan.subscription.status === "cancelled") {
      currentEstimate = estimateUpdate;
    }
  }

  return (
    <>
      <div className="bg-black-100 flex flex-col rounded-xl gap-y-2 py-1">
        <PricingItem
          valueContent={
            currentEstimate &&
            `${formatPrice((currentEstimate.pricePerItem / 100) * includedSeats + currentEstimate.basePrice / 100)}/${formatPricingPeriodInfo(currentPricing)}`
          }
          labelContent={
            <>
              Base price{" "}
              <span className="text-purple-600 text-xs-bold">
                Includes {includedSeats} seats
              </span>
            </>
          }
          isLoading={isLoadingCurrentEstimate}
          tooltipText="The agency plan subscription includes 20 seats. Any seats after the first 20 are priced differently"
        />
        <RenderIf condition={!!currentAdditionalSeats}>
          <PricingItem
            valueContent={
              currentEstimate &&
              `${formatPrice(currentEstimate.pricePerItem / 100)}/seat/${formatPricingPeriodInfo(currentPricing)}`
            }
            labelContent="Additional seats"
            isLoading={isLoadingCurrentEstimate}
          />
        </RenderIf>
      </div>
      <PricingItem
        valueContent={
          isUndefinedOrCancelled ? seatCount : agencyPlan.license_count
        }
        labelContent={
          isUndefinedOrCancelled ? "No. of seats" : "Current no. of seats"
        }
        isLoading={isLoadingCurrentEstimate}
      />
      <PricingItem
        valueContent={
          currentEstimate &&
          `${formatPrice((currentEstimate.pricePerItem / 100) * includedSeats + currentEstimate.basePrice / 100)}/${formatPricingPeriodInfo(currentPricing)}`
        }
        labelContent={
          isUndefinedOrCancelled ? "Plan price" : "Current plan price"
        }
        isLoading={isLoadingCurrentEstimate}
      />
      <RenderIf condition={isChange}>
        <div className="w-full h-px bg-black-200" />
        <RenderIf condition={isPricingPeriodChanged}>
          <div className="bg-purple-50 flex flex-col rounded-xl gap-y-2 py-1">
            <PricingItem
              valueContent={
                estimateUpdate &&
                `${formatPrice((estimateUpdate.pricePerItem / 100) * includedSeats + estimateUpdate.basePrice / 100)}/${formatPricingPeriodInfo(selectionPricing)}`
              }
              labelContent={
                <>
                  New base price{" "}
                  <span className="text-purple-600 text-xs-bold">
                    Includes {includedSeats} seats
                  </span>
                </>
              }
              isLoading={isLoadingEstimateUpdate}
            />
            {!!selectionAdditionalSeats && (
              <PricingItem
                valueContent={
                  estimateUpdate &&
                  `${formatPrice(estimateUpdate.pricePerItem / 100)}/seat/${formatPricingPeriodInfo(selectionPricing)}`
                }
                labelContent="Additional seats"
                isLoading={isLoadingEstimateUpdate}
              />
            )}
          </div>
        </RenderIf>
        <PricingItem
          labelContent="New no. of seats"
          valueContent={
            <span className="text-black-950 text-md-bold">{seatCount}</span>
          }
          isLoading={false}
        />
        <PricingItem
          labelContent="New plan price"
          valueContent={
            estimateUpdate && (
              <span className="text-black-950 text-md-bold">
                {formatPrice(estimateUpdate.amountDue / 100)}/
                {formatPricingPeriodInfo(selectionPricing)}
              </span>
            )
          }
          isLoading={isLoadingEstimateUpdate}
        />
      </RenderIf>
      {alertsComponent}
      <ToPayNow
        estimate={estimateCreation ? estimateCreation : proRatedEstimateUpdate}
        isLoading={isLoadingEstimateCreation || isLoadingProratedEstimateUpdate}
        isDowngradeOrEqual={
          isDowngrade || (!isChange && !isUndefinedOrCancelled)
        }
        isInTrial={false}
      />
      {error && (
        <span className="text-red-500 text-xs">
          {error instanceof HTTPError && error.serverMessage
            ? error.serverMessage
            : "Error loading estimate, please try again later."}
        </span>
      )}
    </>
  );
}
